import { Listbox, createListCollection } from "@chakra-ui/react"
import React, { useEffect, useMemo, useState } from "react"

function flattenLeafItems(variables, items = []) {
  for (const key of Object.keys(variables)) {
    const node = variables[key]
    if (node.variables) {
      flattenLeafItems(node.variables, items)
    } else {
      items.push({
        value: String(node.index),
        label: node.label,
        variable: node
      })
    }
  }
  return items
}

function renderVariableNodes(
  variables,
  itemsByValue,
  activeVariableIndex,
  setActiveNode
) {
  return Object.keys(variables).map((key, i) => {
    const node = variables[key]

    if (node.variables) {
      return (
        <Listbox.ItemGroup
          key={key}
          borderColor="whiteAlpha.300"
          css={{
            "& ~ &": {
              borderTopWidth: "1px"
            },
            "& &": {
              pl: 4
            }
          }}
        >
          <Listbox.ItemGroupLabel
            color="whiteAlpha.800"
            fontWeight="bold"
            textTransform="uppercase"
          >
            {node.label}
          </Listbox.ItemGroupLabel>
          {renderVariableNodes(
            node.variables,
            itemsByValue,
            activeVariableIndex,
            setActiveNode
          )}
        </Listbox.ItemGroup>
      )
    }

    const item = itemsByValue.get(String(node.index))
    if (!item) return null

    // Zag only sets data-highlighted when the listbox itself is focus-visible.
    // Focus stays on the text input, so style the active item from our index.
    const isActive = node.index === activeVariableIndex

    return (
      <Listbox.Item
        key={key}
        item={item}
        ref={isActive ? setActiveNode : null}
        highlightOnHover
        color="white"
        pl={4}
        borderRadius="md"
        cursor="pointer"
        transition="none"
        bg={isActive ? "whiteAlpha.300" : "transparent"}
        _hover={{ bg: "whiteAlpha.300" }}
      >
        <Listbox.ItemText w="full" textAlign="left">
          {node.label}
        </Listbox.ItemText>
      </Listbox.Item>
    )
  })
}

const VariableSelectorList = React.memo(function VariableSelectorList({
  variable,
  variables,
  activeVariableIndex,
  handleSelectVariable,
  EmptyVariableSelectorList
}) {
  const [activeNode, setActiveNode] = useState(null)

  // Keep the active item in view while arrowing through the list.
  useEffect(() => {
    if (activeNode) {
      activeNode.scrollIntoView({
        behavior: "auto", // "auto" for "undetectable" scrolling
        block: "center"
      })
    }
  }, [activeNode])

  const collection = useMemo(
    () => createListCollection({ items: flattenLeafItems(variables) }),
    [variables]
  )

  const itemsByValue = useMemo(() => {
    const map = new Map()
    for (const item of collection.items) {
      map.set(item.value, item)
    }
    return map
  }, [collection])

  const highlightedValue =
    activeVariableIndex != null && itemsByValue.has(String(activeVariableIndex))
      ? String(activeVariableIndex)
      : null

  return (
    <Listbox.Root
      collection={collection}
      selectionMode="single"
      variant="plain"
      value={[]}
      highlightedValue={highlightedValue}
      typeahead={false}
      onSelect={({ value }) => {
        const item = itemsByValue.get(value)
        if (item?.variable) handleSelectVariable(item.variable)
      }}
      gap={0}
      w="full"
    >
      <Listbox.Content
        p={0}
        gap={0}
        maxH="none"
        overflow="visible"
        outline="none"
        bg="transparent"
        borderWidth={0}
      >
        {Object.keys(variables).length
          ? renderVariableNodes(
              variables,
              itemsByValue,
              activeVariableIndex,
              setActiveNode
            )
          : null}
        <Listbox.Empty>
          {EmptyVariableSelectorList ? (
            <EmptyVariableSelectorList variable={variable} />
          ) : null}
        </Listbox.Empty>
      </Listbox.Content>
    </Listbox.Root>
  )
})

VariableSelectorList.displayName = "VariableSelectorList"

export default VariableSelectorList
